'use client';

import { BlockRoot, PortalLink, tx } from '@akademi/site-builder-runtime';
import { TileGlyph, TILE_ICONS, type TileIcon } from '../home/sceneArt';
import { TradeStyles } from '../styles/TradeStyles';
import { HOME_CSS } from '../styles/homeCss';

type Localized = string | Record<string, string>;

export interface SolarCategoryTile {
  name: Localized;
  href: string;
  note?: Localized;
  /**
   * A line drawing for the tile, one of the theme's own `TILE_ICONS`. Not `icon`: that
   * name means a lucide icon everywhere else in the site builder, and the template's
   * icon check rightly rejects anything outside the runtime's curated set.
   */
  glyph?: TileIcon;
}

export interface SolarCategoryTilesProps {
  kicker?: Localized;
  title?: Localized;
  items?: SolarCategoryTile[];
  seeAllLabel?: Localized;
  seeAllHref?: string;
}

/**
 * Shop by department, in Horizon's tile anatomy (`solar/docs/design/redesign-2026-10-01/`,
 * `index.html` `.cat-grid`): one card per department with a line drawing, the name, a line of
 * type and an arrow — compact horizontal rows on phones, three-up cards from tablet width.
 *
 * <p>Drawings rather than photographs because the catalogue has no category imagery yet, and
 * the honest version of "no photograph" is a drawing in the shop's colours, not a grey box.
 * No product counts: this is page data applied once, so a count would be frozen the moment it
 * was typed and then read as stock.
 *
 * <p>An empty `items` renders nothing rather than a heading over an empty grid, which on a home
 * page reads as "this shop has no categories".
 */
export function SolarCategoryTiles({
  kicker,
  title = 'Shop by category',
  items = [],
  seeAllLabel,
  seeAllHref,
}: SolarCategoryTilesProps) {
  if (!items.length) return null;

  return (
    <BlockRoot as="section" className="ae ae-wrap ae-sec">
      <TradeStyles id="home" css={HOME_CSS} />
      <div className="ae-sec__head">
        <div>
          {tx(kicker) ? <p className="ae-eyebrow">{tx(kicker)}</p> : null}
          <h2 className="ae-sec__title">{tx(title)}</h2>
        </div>
        {seeAllHref && tx(seeAllLabel) ? (
          <PortalLink href={seeAllHref} className="ae-sec__more">
            {tx(seeAllLabel)} →
          </PortalLink>
        ) : null}
      </div>
      <ul className="ae-tiles">
        {items.map((item, index) => (
          <li key={index}>
            <PortalLink href={item.href} className="ae-tile">
              {item.glyph && TILE_ICONS.includes(item.glyph) ? (
                <span className="ae-tile__icon">
                  <TileGlyph icon={item.glyph} />
                </span>
              ) : null}
              <span className="ae-tile__body">
                <span className="ae-tile__name">{tx(item.name)}</span>
                {tx(item.note) ? <span className="ae-tile__note">{tx(item.note)}</span> : null}
              </span>
              <span className="ae-tile__arrow" aria-hidden="true">
                →
              </span>
            </PortalLink>
          </li>
        ))}
      </ul>
    </BlockRoot>
  );
}
